import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUnionContacts, searchUnionContacts } from 'model/contact';
import { getAreas } from 'model/room';
import { RootState } from 'model';
import { AreaV2, RoomSchedule, RoomV2 } from 'type/room';
import { ContactMemberV2, ContactV2 } from 'type/contact';
import { setCheckedAreas, getCheckedAreas, clearCheckedAreas, getStaffId } from 'data/storage';
import { IMeetingOrderFn, IMeetingOrderForm, OrderSettings } from 'type/meeting';
import { RoomScheduleFilterForm, RoomScheduleService } from 'nicehubs-plus';
import { Room, Meeting, Recommend } from 'api';
import type { V2MeetingRecommendForm } from 'api/recommend';
import { useListenScheduleChange } from './useListenSchedule';

export interface RoomScheduleRef {
  /** 日程表默认筛选参数 */
  filterValue?: RoomScheduleFilterForm;
  /** 需要闪烁的日程 */
  blinkList: string[];
  /** 预约设置 */
  orderSettings: OrderSettings;
  /** 区域数据 */
  areaData: AreaV2[];
  /** 成员数据 */
  memberData: ContactV2;
  /** 筛选参会人 */
  filterMember: (keyword?: string) => void;
  /** 禁用成员 */
  disabledMember: (node: ContactV2 | ContactMemberV2) => boolean;
  /** 点击预约 */
  confirm: IMeetingOrderFn;
  /** 点击取消 */
  cancel: () => void;
  /** 点击更多 */
  clickMore: (form: IMeetingOrderForm) => void;
  /** 日程服务 */
  service: RoomScheduleService;
  /** AI 推荐开关 */
  aiRecommendEnabled: boolean;
  /** 完整预约推荐会议室候选 */
  roomData?: RoomV2[];
  /** 快速预约推荐会议室候选 */
  recommendRoomCandidates?: RoomV2[];

  addEventListener: any;
  removeEventListener: any;
}
interface UseScheduleTableOptions {
  aiRecommendEnabled?: boolean;
  initialRecommendParams?: Partial<V2MeetingRecommendForm>;
  initialRecommendRequestKey?: string;
  syncCheckedAreas?: boolean;
  syncCheckedAreasAfterRoomDialogOpened?: boolean;
}

const ROOM_SCHEDULE_STATUS_MAP: Record<string, RoomSchedule['status']> = {
  BEFORE: 1,
  DURING: 2,
  FINISH: 3,
  CANCEL: 4,
  DELETE: 4
};

const normalizeRoomScheduleStatus = (status: unknown): RoomSchedule['status'] => {
  if (typeof status !== 'string') return status as RoomSchedule['status'];

  const normalizedStatus = ROOM_SCHEDULE_STATUS_MAP[status];
  if (normalizedStatus) return normalizedStatus;

  const numberStatus = Number(status);
  return Number.isNaN(numberStatus) ? (status as RoomSchedule['status']) : numberStatus;
};

const normalizeRoomSchedule = (schedule: RoomSchedule): RoomSchedule => ({
  ...schedule,
  status: normalizeRoomScheduleStatus(schedule.status)
});

export const useScheduleTable = <T>(options: UseScheduleTableOptions = {}) => {
  const dispatch = useDispatch();
  const componentRef = useRef<RoomScheduleRef & T>();
  const { aiRecommendEnabled = true, initialRecommendParams, initialRecommendRequestKey = '', syncCheckedAreas = true, syncCheckedAreasAfterRoomDialogOpened = false } = options;
  const roomDialogOpenedRef = useRef(false);
  const [earlyScheduleDay, setEarlyScheduleDay] = useState(0);
  useEffect(() => {
    Meeting.getUserMeetingOrderConfig()
      .toPromise()
      .then((res) => {
        console.log('获取用户会议预约配置成功 ===>', res.data);
        if (res.data.earlySchedule) {
          setEarlyScheduleDay(res.data.earlyScheduleDay || 0);
        }
      })
      .catch((err) => {
        console.error('获取用户会议预约配置失败 ===>', err);
      });
  }, []);

  const orderSettings = useSelector((state: RootState) => state.meeting.orderSettings);
  const areaData = useSelector((state: RootState) => state.room.area);
  const unionContacts = useSelector((state: RootState) => state.contact.unionContacts);
  const [displayContacts, setDisplayContacts] = useState<ContactV2>();
  const [blinkList, setBlinkList] = useState<string[]>([]);
  const [filterValue, setFilterValue] = useState<RoomScheduleFilterForm>(() => {
    const checkAreas = getCheckedAreas();
    return { areaIds: checkAreas ? checkAreas.split(',') : undefined };
  });
  const { addListener, removeListener, emitScheduleChange } = useListenScheduleChange();
  const roomListRef = useRef<RoomV2[]>([]);
  const recommendRequestRef = useRef<{
    signature: string;
    promise: ReturnType<ReturnType<typeof Recommend.recommendMeeting>['toPromise']>;
  }>();
  const initialRecommendParamsRef = useRef<Partial<V2MeetingRecommendForm>>();
  const initialRecommendRequestKeyRef = useRef('');
  const consumedInitialRecommendRequestKeyRef = useRef('');
  if (initialRecommendRequestKey && initialRecommendRequestKey !== consumedInitialRecommendRequestKeyRef.current) {
    initialRecommendParamsRef.current = initialRecommendParams;
    initialRecommendRequestKeyRef.current = initialRecommendRequestKey;
  } else {
    initialRecommendParamsRef.current = undefined;
    initialRecommendRequestKeyRef.current = initialRecommendRequestKey;
  }
  const syncRecommendRoomCandidates = useCallback(() => {
    if (!componentRef.current) return;
    componentRef.current.roomData = roomListRef.current;
    componentRef.current.recommendRoomCandidates = roomListRef.current;
  }, []);

  /** 搜索参会人 */
  const filterMember = useCallback(
    (keyword?: string) => {
      if (!keyword) {
        componentRef.current.nodeOrder = 'teamFirst';
        if (unionContacts) {
          setDisplayContacts(unionContacts);
        } else {
          dispatch(
            getUnionContacts({
              callback: (data) => {
                setDisplayContacts(data);
              }
            })
          );
        }
      } else {
        componentRef.current.nodeOrder = 'menberFirst';
        dispatch(
          searchUnionContacts({
            keyword,
            callback: (data) => {
              setDisplayContacts(data);
            }
          })
        );
      }
    },
    [unionContacts]
  );

  /** 禁止选择自己 */
  const disabledMember = (node: ContactV2 | ContactMemberV2) => {
    return node.refId === getStaffId();
  };

  const init = async () => {
    dispatch(getAreas());
    dispatch(
      getUnionContacts({
        callback: (data) => {
          setDisplayContacts(data);
        }
      })
    );
  };

  /** 设置会议室筛选参数 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.filterValue = filterValue;
  }, [filterValue]);

  /** 设置日程条闪烁属性 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.blinkList = blinkList;
  }, [blinkList]);

  /** 设置会议室、会议室日程表格属性 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.areaData = areaData;
  }, [areaData]);

  /** 设置成员选择属性 */
  useEffect(() => {
    if (!componentRef.current || !displayContacts) return;
    componentRef.current.memberData = displayContacts;
  }, [displayContacts]);

  /** 设置企业会议预约配置属性 */
  useEffect(() => {
    if (!componentRef.current || !orderSettings) return;
    componentRef.current.orderSettings = orderSettings;
  }, [orderSettings]);

  /** 筛选参会人 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.filterMember = filterMember;
  }, [filterMember]);

  /** 获取会议室 */
  const getRoomsV2: RoomScheduleService['getRoomsV2'] = async (params) => {
    try {
      const canSyncCheckedAreas = syncCheckedAreas && (!syncCheckedAreasAfterRoomDialogOpened || roomDialogOpenedRef.current);
      if (canSyncCheckedAreas && Object.prototype.hasOwnProperty.call(params, 'areaIdList')) {
        // 当前选中
        const currentCheckAreas = params.areaIdList?.join(',') || '';
        // 筛选记忆
        currentCheckAreas ? setCheckedAreas(currentCheckAreas) : clearCheckedAreas();
      }
    } catch (err) {
      console.error('记录空间筛选结果失败', err);
    }
    const res = await Room.getBookRoomV2(params).toPromise();
    roomListRef.current = res?.data?.data || [];
    syncRecommendRoomCandidates();
    return res.data;
  };

  /** 获取会议室会务选项 */
  const getRoomServiceConfig: RoomScheduleService['getRoomServiceConfig'] = async (params) => {
    const res = await Room.getRoomServiceConfig(params).toPromise();
    return res.data;
  };

  /** 获取会议室日程 */
  const getRoomSchedulesV2: RoomScheduleService['getRoomSchedulesV2'] = async (params) => {
    const res = await Room.getRoomSchedulesV2(params).toPromise();
    const schedules = Array.isArray(res.data) ? res.data : [];
    return schedules.map(normalizeRoomSchedule);
  };

  /* 获取人员日程  */
  const getMemberSchedules: RoomScheduleService['getMemberSchedules'] = async (params) => {
    const roomRes = await Meeting.getMemberSchedules(params).toPromise();
    return roomRes.data;
  };

  /** 获取会议推荐 */
  const recommendMeeting: RoomScheduleService['recommendMeeting'] = async (params) => {
    const initialParams = initialRecommendParamsRef.current;
    const recommendParams = initialParams ? { ...params, ...initialParams } : params;
    if (initialParams) {
      consumedInitialRecommendRequestKeyRef.current = initialRecommendRequestKeyRef.current;
      initialRecommendParamsRef.current = undefined;
    }
    const signature = JSON.stringify(recommendParams);
    if (recommendRequestRef.current?.signature === signature) {
      return recommendRequestRef.current.promise;
    }

    const promise = Recommend.recommendMeeting(recommendParams as Parameters<typeof Recommend.recommendMeeting>[0])
      .toPromise()
      .finally(() => {
        if (recommendRequestRef.current?.signature === signature) {
          recommendRequestRef.current = undefined;
        }
      });
    recommendRequestRef.current = { signature, promise };
    return promise;
  };

  /** 会议室选择弹窗显示/隐藏 */
  const onRoomDialogVisibleChange = ({ detail }: { detail: [boolean] }) => {
    // 会议室表单打开弹窗时，使用最新的区域数据
    const visible = detail[0];
    if (visible) roomDialogOpenedRef.current = true;
    if (visible && componentRef.current) {
      const checkAreas = getCheckedAreas();
      setFilterValue({ areaIds: checkAreas ? checkAreas.split(',') : undefined });
    }
  };

  // 判断会议室是否禁用
  const isDisabledRoom = (roomId: string) => {
    const disabledRoomList = ['THIRD_PARTY_SYNC_ENTERPRISE_WECHAT'];
    const appId = roomListRef.current.find((room) => room.id === roomId)?.appId || '';
    return disabledRoomList.includes(appId);
  };

  /** 属性设置、事件监听 */
  useEffect(() => {
    init();
    if (!componentRef.current) return;
    componentRef.current.disabledMember = disabledMember;
    componentRef.current.aiRecommendEnabled = aiRecommendEnabled;
    syncRecommendRoomCandidates();

    /** 日程相关服务 */
    componentRef.current.service = {
      getRoomsV2,
      getRoomSchedulesV2,
      getMemberSchedules,
      recommendMeeting,
      addScheduleListener: addListener,
      getRoomServiceConfig
      /** 触发时机不对，使用 useEffect return 移除监听 */
      // removeScheduleListener: removeListener
    };

    // 监听变化
    componentRef.current?.addEventListener('room-dialog-visible-change', onRoomDialogVisibleChange);

    return () => {
      removeListener();
      componentRef.current?.removeEventListener('room-dialog-visible-change', onRoomDialogVisibleChange);
    };
  }, []);

  return {
    componentRef,
    setBlinkList,
    emitScheduleChange,
    isDisabledRoom,
    earlyScheduleDay
  };
};

export default useScheduleTable;
